import React, { useEffect, useState } from "react";
import { Box, Tooltip, IconButton, CircularProgress, Alert, Button, Snackbar } from "@mui/material";
import Datagrid from "../../components/Datagrid/Datagrid";
import PageTitle from "../../components/PageTitle/PageTitle";
import { useSite } from "../../context/siteContext";
import customHttp from "../../services/customHTTP";
import ContextFilterAndSort from "../../components/ContextFilterAndSort/ContextFilterAndSort";
import OrderTracker from "../../components/OrderTracker/OrderTracker";
import OrderDetails from "../../components/OrderDetails/OrderDetails";
import FindInPageOutlinedIcon from "@mui/icons-material/FindInPageOutlined";
import "./OrderHistory.css";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import { useCart } from "../../context/cartContext";
// OrderHistory Component
const OrderHistory = () => {
	const { selectedSite, currencySymbol } = useSite();
	const { addToCart, addMultipleToCart } = useCart();
	const [orders, setOrders] = useState();
	const [fullOrdersData, setFullOrdersData] = useState();
	const [selectedOrder, setSelectedOrder] = useState();
	const [filteredOrders, setFilteredOrders] = useState();
	const [searchQuery, setSearchQuery] = useState("");
	const [alertisSuccess, setAlertIsSuccess] = useState(false); // To track success state
	const [alertMessage, setAlertMessage] = useState(""); // To track success state
	const [openAlert, setOpenAlert] = useState(false); // To control the alert visibility
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const formatDate = (dateString) => {
		const date = new Date(dateString);
		const month = `0${date.getMonth() + 1}`.slice(-2); // Get month (01-12)
		const day = `0${date.getDate()}`.slice(-2); // Get day (01-31)
		const year = date.getFullYear(); // Get full year
		const hours = `0${date.getHours()}`.slice(-2); // Get hours (00-23)
		const minutes = `0${date.getMinutes()}`.slice(-2); // Get minutes (00-59)

		return `${year}-${month}-${day} ${hours}:${minutes}`; // Return formatted date
	};

	useEffect(() => {
		const fetchOrders = async () => {
			try {
				const req_body = { site_number: selectedSite };
				const response = await customHttp.post("/orders/getOrdersBySite", req_body);

				if (response.data && response.data.success) {
					const orderDetailsArray = response.data.data;

					const allOrders = orderDetailsArray.map((data) => ({
						order_id: data.order.order_id,
						order_number: data.order.order_number,
						user_display_name: data.order.user_display_name,
						site_number: data.order.site_number,
						order_total: data.order.order_total,
						order_status: data.order.order_status,
						order_created_date: formatDate(data.order.order_created_date),
						order_update_date: formatDate(data.order.order_update_date),
						order_currency: data.order.order_currency,
						order_payment_mode: data.order.order_payment_mode,
						order_po: data?.details?.po_number || "",
						order_erp_number: data?.details?.freight_number || "",
						products: data.products,
					}));
					setFullOrdersData(orderDetailsArray);
					setOrders(allOrders);
					setFilteredOrders(allOrders);
					setError(null);
					setSelectedOrder(null);
					setLoading(false);
				} else {
					setError(response.data.message || "No Orders Found for the Site");
					setLoading(false);
				}
			} catch (error) {
				console.error("Error while fetching the order", error);
				setError("An error occurred while fetching the order. Please try again.");
				setLoading(false);
			}
		};

		if (selectedSite) fetchOrders();
	}, [selectedSite]);

	// Utility function to flatten an object into a string for search
	const flattenObjectToString = (obj) => {
		let flatString = "";

		// Recursively traverse the object and build a flat string of values
		const flatten = (value) => {
			if (typeof value === "object" && value !== null) {
				Object.values(value).forEach(flatten);
			} else {
				flatString += ` ${value}`;
			}
		};

		flatten(obj);
		return flatString.toLowerCase(); // Convert to lowercase for case-insensitive search
	};

	const handleSearchChange = (value) => {
		const query = value.toLowerCase();
		setSearchQuery(query);

		// Filter orders based on the search query
		const filtered = orders.filter((order) => flattenObjectToString(order).includes(query));

		setFilteredOrders(filtered);
	};
	const handleViewOrder = (order) => {
		setSelectedOrder(order); // Set the selected order to display its details
	};

	const handleCloseOrderDetails = () => {
		setSelectedOrder(null);
	};

	const renderOrderDetails = () => {
		if (!selectedOrder) return null;
		const filteredOrder = fullOrdersData.find((order) => order.order.order_id === selectedOrder.order_id);
		console.log(filteredOrder);
		const { order, details } = filteredOrder;

		return (
			<div>
				<PageTitle
					title={
						details.freight_number !== ""
							? `Order Details: Order # ${details.freight_number}`
							: `Quote Details: Quote # ${order.order_number}`
					}
				/>
				<div className="track-order-container">
					<Button variant="outlined" color="primary" onClick={handleCloseOrderDetails}>
						Close
					</Button>
					<Button
						variant="contained"
						startIcon={<AddShoppingCartIcon />}
						color="primary"
						onClick={() => {
							handleOrderAgain(selectedOrder);
						}}>
						Buy Again
					</Button>
				</div>
				<div className="order-tracker-container"></div>
				<OrderTracker orderData={filteredOrder} />
				<OrderDetails orderData={filteredOrder} />

				<div className="all-orders-container">
					<Button
						variant="contained"
						startIcon={<AddShoppingCartIcon />}
						color="primary"
						onClick={() => {
							handleOrderAgain(selectedOrder);
						}}>
						Buy Again
					</Button>
					<Button variant="outlined" color="primary" onClick={handleCloseOrderDetails}>
						Close
					</Button>
				</div>
			</div>
		);
	};

	const handleOrderAgain = async (row) => {
		console.log(row);
		const products = row.products;
		const cartAdd = products
			.map((product) => {
				if (product.productsData && product.productsData.purchasable === 1) {
					return { ProductID: product.product_id, Quantity: product.product_quantity, Site: selectedSite };
				}
				// console.log(product);
				return null; // Return null explicitly for non-purchasable products
			})
			.filter((item) => item !== null); // Remove null values from the array

		if (cartAdd.length === 0) {
			console.warn("No valid products to add to cart.");
			return;
		}
		await addMultipleToCart(cartAdd);
		setAlertMessage(`Part(s) from ${row.order_number} have been added to Cart`);
		setAlertIsSuccess(true);
		setOpenAlert(true);

		setTimeout(() => {
			setOpenAlert(false);
			setAlertMessage("");
		}, 2000);
	};

	// Define columns for the Datagrid
	const columns = [
		{
			field: "order_number",
			headerName: "Quote Number",
			flex: 1,
			renderCell: (params) => (
				<Tooltip title="View Quote Details & Tacking Information">
					<span
						style={{
							cursor: "pointer",
							fontWeight: "bold",
						}}
						onMouseEnter={(e) => (e.target.style.fontWeight = "bold")}
						onMouseLeave={(e) => (e.target.style.fontWeight = "bold")}>
						{params.value}
					</span>
				</Tooltip>
			),
		},
		{
			field: "order_erp_number",
			headerName: "Order Number",
			flex: 1,
			renderCell: (params) => (
				<Tooltip
					title={
						params.value ? "View Order Details & Tacking Information" : "Quote is not converted to Order"
					}>
					<span
						style={{
							cursor: "pointer",
							fontWeight: params.value ? "normal" : "bold",
						}}
						onMouseEnter={(e) => (e.target.style.fontWeight = params.value ? "normal" : "bold")}
						onMouseLeave={(e) => (e.target.style.fontWeight = params.value ? "normal" : "bold")}>
						{params.value || "N/A"}
					</span>
				</Tooltip>
			),
		},
		{ field: "order_payment_mode", headerName: "Payment Mode" },
		{ field: "order_po", headerName: "PO", width: 150 },
		{ field: "user_display_name", headerName: "User" },
		{
			field: "order_total",
			headerName: "Total",
			width: 120,
			renderCell: (params) => `${currencySymbol}${params.value}`,
		},
		{
			field: "order_currency",
			headerName: "Currency",
			width: 100,
			renderCell: (params) => params.value?.toUpperCase() || "USD",
		},
		{ field: "order_status", headerName: "Status", width: 120 },
		{
			field: "order_created_date",
			headerName: "Created Date",
			valueFormatter: (value) => formatDate(value),
			width: 150,
		},
		{
			headerName: "Order Again",
			width: 100,
			sortable: false,
			headerAlign: "center",
			align: "center",
			renderCell: (params) => (
				<div>
					<Tooltip title="Order Again">
						<IconButton
							color="primary"
							aria-label="Order Again"
							onClick={(event) => {
								event.stopPropagation();
								handleOrderAgain(params.row);
							}}>
							<AddShoppingCartIcon />
						</IconButton>
					</Tooltip>
				</div>
			),
		},
	];

	const handleRowClick = (params) => {
		handleViewOrder(params.row);
	};

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
				<CircularProgress />
			</Box>
		);
	}

	if (error && !loading) {
		return (
			<Alert severity="info" className="error-alert">
				{error}
			</Alert>
		);
	}

	return (
		<div className="order-history-container">
			<PageTitle title="Order History" />

			<div className="orders-serach-container">
				<Box sx={{ width: { xs: "100%", md: "20%" } }}>
					<ContextFilterAndSort
						value={searchQuery}
						onSearchChange={handleSearchChange}
						placeholder="Search Quotes/Orders"
						showSearch={true}
						showSort={false}
					/>
				</Box>
			</div>
			<Datagrid
				key="orderHistoryGrid"
				data={filteredOrders}
				columns={columns}
				rowClick={true}
				onRowClick={handleRowClick}
				pageSize={10}
				showSearchBar={false}
				checkboxSelection={false}
				rowIdField="order_id"
				sortBy={[{ field: "order_created_date", sort: "desc" }]}
			/>
			{renderOrderDetails()}
			<Snackbar
				open={openAlert}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				autoHideDuration={3000}
				onClose={() => setOpenAlert(false)}
				className="SnackBar">
				<Alert severity={alertisSuccess ? "success" : "error"} sx={{ width: "100%" }}>
					{alertMessage}
				</Alert>
			</Snackbar>
		</div>
	);
};

export default OrderHistory;
